// frontend/src/pages/storeActivity/text.js
//
// The non-component readings the Store Activity sections share.

// What a column the store never filled in renders as. A blank cell on a
// support screen reads as a rendering bug; "we hold nothing here" is an
// answer, and on this page it is often the answer being looked for.
export const EMPTY = '—';

/**
 * Human-readable name for an event type.
 *
 * `defaultValue` is deliberate here, unlike SourceChip's closed vocabulary:
 * the table holds event types from every version the store has lived through,
 * including ones since renamed or retired (`po_items_exported`). Rendering the
 * raw code for those is honest — dropping the row, or blanking its name, would
 * hide history the page exists to show.
 */
export function eventLabel(eventType, t) {
  return t(`storeActivity.events.${eventType}`, { defaultValue: eventType });
}

/**
 * Plain names for a scheduled pipeline and for one of its steps. Same
 * `defaultValue` reasoning as `eventLabel`: old timeline rows carry the
 * names a pipeline had when it ran, including ones since renamed.
 */
export function pipelineLabel(pipeline, t) {
  return t(`storeActivity.pipelines.${pipeline}`, { defaultValue: pipeline });
}

export function stepLabel(step, t) {
  return t(`storeActivity.steps.${step}`, { defaultValue: step });
}

/**
 * Whether a timeline row is scheduled work that went as planned — the
 * rows the timeline hides until asked: an automation row whose `status` is
 * `succeeded` (a finished run or synchronization). Every other automation
 * row stays in view — a failed or partly failed run, a failed
 * synchronization, and a requeued job, which carries no `status` at all.
 */
export function isRoutineAutomation(row) {
  return row.category === 'automation' && row.properties.status === 'succeeded';
}

/**
 * How the store's Shopify plan reads: `{ label, isDevelopment }`.
 *
 * The backend sends the reading as three values rather than a sentence
 * (`api/views/analytics.py:_store_profile`) so this page can localize it and
 * colour the development case. The rule it mirrors is `core/shop_plan.py`,
 * which words the same reading for the internal notices — one reading, two
 * audiences: that one is English plaintext in a team inbox, this one is
 * translated and on screen.
 *
 * `partner_development` is `null` until the shop has been observed, and that
 * stays a distinct answer here. Collapsing it into "not a development store"
 * would report every row predating the columns as a real merchant, which is
 * the direction of the mistake that costs money.
 *
 * The observation date is part of the label, never dropped: nothing refreshes
 * the reading between installs, so an undated plan would read as current when
 * it is only the last thing we saw.
 */
export function shopPlanReading(profile, t, formatDateTime) {
  if (!profile.plan_applicable) {
    return { label: t('storeActivity.connection.planNotShopify'), isDevelopment: false };
  }
  // Tested positively rather than by listing the falsy shapes: the contract
  // is exactly true / false / null, and anything else — a key the API stopped
  // sending, a payload shape change — must land on "unobserved" rather than
  // fall through to the unmarked case and report a development store as a
  // real merchant.
  const isDevelopment = profile.plan_partner_development;
  if (isDevelopment !== true && isDevelopment !== false) {
    return { label: t('storeActivity.connection.planUnobserved'), isDevelopment: false };
  }

  const named = isDevelopment
    ? t('storeActivity.connection.planDevelopment')
    : t('storeActivity.connection.planReal');
  return {
    isDevelopment,
    label: t('storeActivity.connection.planObserved', {
      plan: profile.plan_public_name || t('storeActivity.connection.planUnnamed'),
      classification: named,
      when: formatDateTime(profile.plan_observed_at),
    }),
  };
}
